import * as PopoverPrimitive from '@radix-ui/react-popover'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import type { TransKey } from '@/i18n'
import { cn } from '@/lib/utils'

import type { TourPlacement } from './steps'
import { ONBOARDING_TOUR_TOTAL_STEPS } from './steps'

type TourCardProps = {
  stepIndex: number
  titleKey: TransKey
  bodyKey: TransKey
  buttonKey: TransKey
  placement: TourPlacement
  targetRect: DOMRect | null
  Illustration: React.ComponentType<{ className?: string }>
  onContinue: () => void
  isContinuing?: boolean
}

function CardBody({
  stepIndex,
  titleKey,
  bodyKey,
  buttonKey,
  Illustration,
  onContinue,
  isContinuing,
}: Omit<TourCardProps, 'placement' | 'targetRect'>) {
  const { t } = useTranslation()
  const progress = ((stepIndex + 1) / ONBOARDING_TOUR_TOTAL_STEPS) * 100

  return (
    <div
      key={stepIndex}
      className='animate-in fade-in-0 zoom-in-95 flex w-full flex-col items-center gap-4 px-4 pt-2 pb-4 text-center duration-200'
    >
      <Illustration className='size-[143px] shrink-0' />
      <h2 className='text-text-primary text-lg leading-7 font-medium'>{t(titleKey)}</h2>
      <p className='text-text-secondary text-sm leading-6'>{t(bodyKey)}</p>
      <Button variant='secondary' size='sm' onClick={onContinue} disabled={isContinuing} className='min-w-[68px]'>
        {t(buttonKey)}
      </Button>
      <div className='flex w-full flex-col gap-3'>
        <div className='bg-border-default h-1 w-full overflow-hidden rounded-full'>
          <div className='h-full rounded-full bg-[#00AEFF] transition-[width] duration-500 ease-out' style={{ width: `${progress}%` }} />
        </div>
        <p className='text-text-secondary text-end text-sm'>
          {t('onboarding.stepCounter', { current: stepIndex + 1, total: ONBOARDING_TOUR_TOTAL_STEPS })}
        </p>
      </div>
    </div>
  )
}

/**
 * Coach-mark card. Anchored to the target via Radix Popover when placement is top/bottom;
 * centered overlay for the final step.
 */
export function TourCard({
  stepIndex,
  titleKey,
  bodyKey,
  buttonKey,
  placement,
  targetRect,
  Illustration,
  onContinue,
  isContinuing,
}: TourCardProps) {
  const anchorRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(true)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const isCentered = placement === 'center'
  const side = placement === 'top' ? 'top' : 'bottom'

  useLayoutEffect(() => {
    if (!anchorRef.current || !targetRect || isCentered) return
    const el = anchorRef.current
    el.style.position = 'fixed'
    el.style.top = `${targetRect.top}px`
    el.style.left = `${targetRect.left}px`
    el.style.width = `${targetRect.width}px`
    el.style.height = `${targetRect.height}px`
    el.style.pointerEvents = 'none'
    el.style.zIndex = '1101'
  }, [targetRect, isCentered])

  // Only remount/reopen when the step (or center mode) changes — not on every scroll rect update.
  useEffect(() => {
    setOpen(false)
    const id = requestAnimationFrame(() => setOpen(true))
    return () => cancelAnimationFrame(id)
  }, [stepIndex, isCentered])

  if (!mounted) return null

  // Wait for the target before showing anchored cards (avoids a centered↔anchored flash).
  if (!isCentered && !targetRect) return null

  const cardClassName =
    'bg-white text-text-primary z-[1102] w-[calc(100vw-2rem)] max-w-[420px] rounded-2xl border border-[#E5E7EB] p-0 shadow-lg outline-none md:max-w-[567px]'

  const cardBodyProps = {
    stepIndex,
    titleKey,
    bodyKey,
    buttonKey,
    Illustration,
    onContinue,
    isContinuing,
  }

  if (isCentered) {
    return createPortal(
      <div className='pointer-events-none fixed inset-0 z-[1102] flex items-center justify-center p-4'>
        <div className={cn(cardClassName, 'animate-in fade-in-0 zoom-in-95 pointer-events-auto duration-200')}>
          <CardBody {...cardBodyProps} />
        </div>
      </div>,
      document.body
    )
  }

  return createPortal(
    <PopoverPrimitive.Root open={open} onOpenChange={() => {}}>
      <PopoverPrimitive.Anchor asChild>
        <div ref={anchorRef} />
      </PopoverPrimitive.Anchor>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side={side}
          align='center'
          sideOffset={16}
          collisionPadding={16}
          avoidCollisions
          onOpenAutoFocus={e => e.preventDefault()}
          className={cn(
            cardClassName,
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:duration-200'
          )}
        >
          <CardBody {...cardBodyProps} />
          <PopoverPrimitive.Arrow className='fill-white' width={16} height={8} />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>,
    document.body
  )
}
